import {
  computeOrthogonalRoute,
  type NodeBox,
  type Point,
  type RouteOptions,
  type RouteResult,
} from './orthogonal-router'
import { type RouteBasis } from './model-basis'

/** The edge's current geometry, as the caller holds it. */
export interface ExistingRoute {
  /** The stored chain. Interior-only for anything imported through the transformer,
   *  which strips the terminals (they are re-anchored per render). */
  points: Point[]
  sourceHandle?: string | undefined
  targetHandle?: string | undefined
}

/** KonvaCanvas.tsx:297-298 applies exactly these when an edge has no handle. */
const DEFAULT_SRC_HANDLE = 'right-src'
const DEFAULT_TGT_HANDLE = 'left-tgt'

/**
 * The single dispatch point for edge routing.
 *
 * Every call site that WRITES geometry goes through here and assigns the result
 * unconditionally. That is the point: no call site carries an `if (iso)`, so none can
 * be missed when a new one is added.
 *
 * `existing` is what lets the isometric branch exist at all. A bare RouteResult has no
 * way to say "leave this alone" — it can only describe a fresh route. Passing the
 * current chain in gives the facade something to hand back.
 *
 * Callers must pass the chain that is correct FOR THEIR OPERATION, which is not always
 * the untouched one: `moveVertices` translates it first (both endpoints move with the
 * selection), `reverseEdge` reverses it (the connection ends just swapped), and
 * `setEdgeWaypoints` passes `undefined` (an explicit user edit must route).
 */
export function routeEdge(
  src: NodeBox,
  tgt: NodeBox,
  options: RouteOptions | undefined,
  existing: ExistingRoute | undefined,
  basis: RouteBasis,
): RouteResult {
  // Isometric geometry is authored, not generated: PIPE-FLO Desktop has no auto-router
  // at all, so re-routing an iso edge can only destroy it. Keeping the chain is
  // sufficient — the canvas re-anchors both terminals from the live vertex on every
  // render (KonvaCanvas.tsx:306-314), so the ends follow a dragged device while the
  // authored geometry survives.
  //
  // Do NOT add a `points.length > 0` condition here. An empty chain is not "nothing to
  // preserve" — it is a STRAIGHT RUN, and preserving it is the whole point: the renderer
  // draws [srcPort, tgtPort] for an empty chain, which is the authored 30° line, whereas
  // the router would return a 2-corner 90° Z. 4 of the 10 edges in the tutorial iso model
  // are exactly this case.
  //
  // ⚠️ A BRAND-NEW iso edge (no `existing`) is ALSO never orthogonally routed. It was,
  // until native iso authoring existed — the old contract routed a new edge regardless
  // of basis, safe only because every iso model was imported and imports never draw new
  // pipes through this facade. Once a model can be AUTHORED isometric, the very first
  // pipe the user draws hit that gap and came back a 90° Z (Dominik, tank→PB on a native
  // iso canvas). A new iso pipe is a STRAIGHT line — an empty chain, `[srcPort, tgtPort]`.
  // That line is isometric only when the two devices share an axis (the stated limit of
  // native iso authoring, surfaced in the create dialog); a straight diagonal is the
  // correct primitive, whereas an orthogonal Z is a shape the model cannot even hold
  // (setEdgeWaypoints rejects the diagonal chain the Z's corners must become on any edit).
  if (basis === 'isometric') {
    return {
      points: existing ? existing.points.map(p => ({ ...p })) : [],
      sourceHandle: existing?.sourceHandle ?? DEFAULT_SRC_HANDLE,
      targetHandle: existing?.targetHandle ?? DEFAULT_TGT_HANDLE,
    }
  }
  return computeOrthogonalRoute(src, tgt, options)
}
